/* The shape of a car, as the API sends it.

   The records themselves live in the API's database and reach the storefront
   over HTTP — this file is the contract, not the data.
   Instalment maths lives in utils/lease.ts and money formatting in
   utils/format.ts, so this file stays a type. */

/* What the quoted variant-year price already covers. The API resolves the
   car's stable trim link and model year before sending it, so every vehicle
   arrives with all four set. */
export type Inclusions = {
  insurance: boolean
  registration: boolean
  withholdingTax: boolean
  tracker: boolean
}

export const DEFAULT_INCLUSIONS: Inclusions = {
  insurance: true,
  registration: true,
  withholdingTax: true,
  tracker: false,
}

/** One paint this car is offered in — and the car standing in it.

    A car is one physical car, so "the colours it comes in" is answered by the
    floor rather than by one record claiming three paints: every car of the
    same make, model, trim and year that has not been sold contributes its
    own colour to this list. Choosing one is therefore opening a different
    car — a different plate number under the same variant-year quote — which
    is why each entry carries the id to go to. */
export type VehicleColour = {
  /** The id of the car standing in this paint. This car's own on its own
      swatch, so the current colour is the one whose id is the car's. */
  id: string
  /** 'Pearl White' — the name the floor says. */
  name: string
  /** '#EDEFF2'. What the swatch is painted. */
  hex: string
  /** False on a car a booking is holding: still offered, not still free. */
  available: boolean
}

/** How this showroom will take an order on a trim in this model year: on the
    spot, on booking, or either. */
export type Availability = 'on-spot' | 'booking' | 'both'

export type Vehicle = {
  id: string
  /** Stable catalogue relationship; display names are shaped alongside it. */
  variantId: string
  brand: string
  /** The model line — "Corolla Altis". */
  model: string
  /** The trim it is sold as — "Grande X 1.8 CVT". */
  variant: string
  /** The model year stamped on this car. */
  year: number
  /** PKR, ex-showroom: what this car is advertised at. Zero when none has
      been set, which is the storefront's cue to say "price on request" rather
      than to print a nought. */
  price: number
  /** Where it stands. A sold car is still reachable by its own link — a
      shared page should not 404 — but nothing offers to book it. */
  status: 'in-stock' | 'reserved' | 'sold'
  /** Gallery-ready: exterior, exterior, detail, interior. The car's only set
      of photographs, whichever paint a visitor is looking at. */
  images: string[]
  /** The paints this car is offered in: its own, and every other colour the
      same trim and model year is standing on the floor in. One entry when it
      is the only one of its kind; empty when nobody has recorded a colour. */
  colours: VehicleColour[]
  /** That paint, as a word — what the spec sheet prints. */
  colour: string
  /** Whether a bank will lease it — the "bank leased" line on the card. */
  financingAvailable: boolean
  /** Which of the two routes this car may be ordered on, as the showroom has
      recorded it. 'both' is the ordinary answer and shows the pair of routes
      on the card and on the car's page; the other two show a single caption
      saying the one way it can be had. */
  availability: Availability

  /* ---------- the two ways an order is taken ---------- */

  /** What a buyer puts down to drive this variant-year away today. Zero when
      the showroom has not settled a figure, which is the storefront's cue to
      say "on request" rather than to print a nought. */
  onSpotDownPayment: number
  /** What it takes to book this variant-year instead — the same car, a different
      first payment. Zero reads the same way as above. */
  bookingDownPayment: number
  /** The monthly figure the showroom quotes on this variant-year. Zero when
      nobody has quoted it; `instalment` below then falls
      back to the house's standard terms. */
  monthlyInstalment: number
  /** How many years that monthly figure runs for. Zero falls back to the
      house's standard tenure. */
  instalmentYears: number
  inclusions: Inclusions
  featured?: boolean
  /** ISO timestamp the car was added to the catalogue. Drives the Newest sort. */
  createdAt: string
  tag?: string

  /* ---------- derived by the API ---------- */

  /** "2026 Toyota Corolla Altis Grande X 1.8" — the name a person says. */
  title: string
  /** One sentence covering the car, its finance and what the price includes. */
  description: string
  /** The deposit the instalment below assumes, in rupees. The showroom's own
      figure on a car whose lease terms were set for it, and thirty per cent of
      the price on every other — resolved by the API, so the card and the
      detail page never quote a monthly figure against a deposit that is not
      the one it was worked out from. Null on a car no bank will lease. */
  deposit: number | null
  /** Indicative monthly instalment, or null on a car no bank will lease. */
  instalment: number | null
}

/** What a car is called, without its year: "Toyota Corolla Altis Grande X
    1.8". Three fields joined in one place, because they are printed together
    in a dozen — an alt text, a WhatsApp message, a modal heading — and a car
    named two ways across them reads as two cars.

    `title` on the record is the same name with the year in front. Use that
    where the year belongs in the sentence and this where it is already
    printed beside it. */
export const nameOf = (vehicle: Pick<Vehicle, 'brand' | 'model' | 'variant'>) =>
  [vehicle.brand, vehicle.model, vehicle.variant].filter(Boolean).join(' ')

/** One option the filter panel offers, and the number of cars behind it. */
export type FacetOption = {
  /** 'Toyota', 'Corolla Altis', '2026' — what the checkbox is labelled. */
  name: string
  /** How many cars on the floor carry it. A count beside a box is what stops
      a visitor ticking their way into an empty grid. */
  count: number
}

/** A paint, with the swatch the console recorded for it. */
export type ColourOption = FacetOption & { hex: string }

/** Everything the filter panel is drawn from, counted from live stock rather
    than hard-coded — and counted by the API, over the same floor it filters,
    so the number beside a box and the number of cars ticking it returns
    cannot disagree. */
export type Facets = {
  brands: FacetOption[]
  /** Narrowed to the chosen brands, so the panel cannot offer an empty result. */
  models: FacetOption[]
  /** Newest model year first — the one people look for. */
  years: FacetOption[]
  colours: ColourOption[]
  /** How many cars carry each thing the quoted price can cover. */
  includes: Record<keyof Inclusions, number>
  /** Every asking price on the floor — what the price histogram is drawn from. */
  prices: number[]
  minPrice: number
  maxPrice: number
  /** The monthly figure the instalment pills divide the floor at. */
  instalmentBand: number
  /** Cars on the unfiltered floor. */
  total: number
}

/** What the panel reads before the API has answered: no options, no range,
    and nothing that would make a control render half-drawn. */
export const EMPTY_FACETS: Facets = {
  brands: [],
  models: [],
  years: [],
  colours: [],
  includes: { insurance: 0, registration: 0, withholdingTax: 0, tracker: 0 },
  prices: [],
  minPrice: 0,
  maxPrice: 0,
  instalmentBand: 0,
  total: 0,
}
